/**
 * Relation-field display helpers for field-display-renderer.
 * Each function renders the appropriate Smart*Display component for a
 * relation field value (object with id, or a bare UUID string).
 * Returns null when the value doesn't match either shape.
 */

import React from "react";
import { SmartUserDisplay } from "@/components/shared/smart-display/SmartUserDisplay";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import { SmartTicketDisplay } from "@/components/shared/smart-display/SmartTicketDisplay";
import { SmartCompanyDisplay } from "@/components/shared/smart-display/SmartCompanyDisplay";
import { isUUID } from "./field-helpers";
import type { FieldValue } from "../types";

/**
 * Type guard for any "relation" field value — i.e. an object carrying an `id`.
 * Returned shape preserves arbitrary additional fields, which Smart*Display
 * components forward through their *Data props.
 */
function isRelationObject(value: FieldValue): value is { id: string } & Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof (value as { id?: unknown }).id === "string"
  );
}

export function renderTicketField(
  value: FieldValue,
  showIcon: boolean,
  className: string,
  disabled: boolean
): React.ReactNode | null {
  if (isRelationObject(value)) {
    return (
      <SmartTicketDisplay
        ticketId={value.id}
        ticketData={{
          id: typeof value.id === "string" ? value.id : undefined,
          ticket_number:
            typeof value.ticket_number === "number" ||
            typeof value.ticket_number === "string"
              ? value.ticket_number
              : null,
          title: typeof value.title === "string" ? value.title : null,
        }}
        showIcon={showIcon}
        className={`inline-block ${className}`}
        disabled={disabled}
        showPopover={false}
      />
    );
  }
  if (isUUID(String(value))) {
    return (
      <SmartTicketDisplay
        ticketId={String(value)}
        showIcon={showIcon}
        className={`inline-block ${className}`}
        disabled={disabled}
        showPopover={false}
      />
    );
  }
  return null;
}

export function renderUserField(
  value: FieldValue,
  showIcon: boolean,
  className: string,
  disabled: boolean
): React.ReactNode | null {
  if (isRelationObject(value)) {
    return (
      <SmartUserDisplay
        userId={value.id}
        userData={{
          id: typeof value.id === "string" ? value.id : undefined,
          first_name: typeof value.first_name === "string" ? value.first_name : null,
          last_name: typeof value.last_name === "string" ? value.last_name : null,
          email: typeof value.email === "string" ? value.email : null,
        }}
        showIcon={showIcon}
        className={`inline-block ${className}`}
        disabled={disabled}
        showPopover={false}
      />
    );
  }
  if (isUUID(String(value))) {
    return (
      <SmartUserDisplay
        userId={String(value)}
        showIcon={showIcon}
        className={`inline-block ${className}`}
        disabled={disabled}
        showPopover={false}
      />
    );
  }
  return null;
}

/** Used for both contact_id and customer_id — they resolve to the same component. */
export function renderContactField(
  value: FieldValue,
  showIcon: boolean,
  className: string,
  disabled: boolean
): React.ReactNode | null {
  if (isRelationObject(value)) {
    return (
      <SmartContactDisplay
        contactId={value.id}
        contactData={value}
        showIcon={showIcon}
        className={`inline-block ${className}`}
        disabled={disabled}
      />
    );
  }
  if (isUUID(String(value))) {
    return (
      <SmartContactDisplay
        contactId={String(value)}
        showIcon={showIcon}
        className={`inline-block ${className}`}
        disabled={disabled}
      />
    );
  }
  return null;
}

export function renderCompanyField(
  value: FieldValue,
  showIcon: boolean,
  className: string,
  disabled: boolean
): React.ReactNode | null {
  if (isRelationObject(value)) {
    return (
      <SmartCompanyDisplay
        companyId={value.id}
        companyData={value}
        showIcon={showIcon}
        className={`inline-block ${className}`}
        disabled={disabled}
        showPopover={false}
      />
    );
  }
  if (isUUID(String(value))) {
    return (
      <SmartCompanyDisplay
        companyId={String(value)}
        showIcon={showIcon}
        className={`inline-block ${className}`}
        disabled={disabled}
        showPopover={false}
      />
    );
  }
  return null;
}
